import React, { useState, useEffect, useCallback } from 'react';
import Table from '../components/table/table';
import SubNav from '../components/subNav/subNav';
import DeviceData from '../data/devices.json';
const { devicesData } = { ...DeviceData }
import DisplayModal from '../modals/displayModal/displayModal';
import { createDeviceData, updateDeviceData, fetchDeviceData, deleteDeviceData, fetchDeviceGlobalSearchData, uploadBulkDevice, deactivateDevice } from '../api/devicesApi';
import Loader from '../components/loader/loader';
import { exportToExcel } from '../functions/exportToExcel';
import debouce from 'lodash/debounce';
import handle_toast  from '../genericFunctions/toast_Handler';

const Device = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [dataFetched, setDataFlag] = useState(false)
  const [deviceData, setDeviceData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [modalVisible, setModalVisible] = useState(false);
  const [deactivateData, setDeactivateDeviceData] = useState([]);
  const [subNavData, setsubNavData] = useState(devicesData.deviceSubNav ? devicesData.deviceSubNav : [])
  const [deactivateFlag, setDeactivateButtonToActive] = useState(false)
  const accessData = sessionStorage.getItem('accessData') ? JSON.parse(sessionStorage.getItem('accessData'))['Device'] : {};

  const handleClickSelect = (id) => {
    setActiveTab(id);
    setDeviceData([]);
  }

  useEffect(() => {
    fetchDevices();
  }, [activeTab])
  
  // debounce call
  const debouncedCall = useCallback(
    debouce(()=> {
      fetchDevices();
    }, 500), //Adjusting the delay
    []
  );

  useEffect(() => {
    if (!dataFetched) debouncedCall();
    
    //Clean up the debounce on component unmount
    return () => {
      debouncedCall.cancel();
    };
  }, [dataFetched, debouncedCall, activeTab]);

  const fetchDevices = async () => {
    try {
      setIsLoading(true);
      const filterType = activeTab == 1 ? 'allDevices' : 'myDevices'
      const resp = await fetchDeviceData(filterType);
      setDeviceData(resp?.data?.data)
      setIsLoading(false)
      setDataFlag(true)
      handle_toast({message: `Devices data fetched`, status: 'success'})
      if (modalVisible) toggleModal();
    }
    catch (error) {
      setDataFlag(true);
      setIsLoading(false);
      handle_toast(error)
    }
  }

  const fetchGlobalSearch = async (query) => {
    try {
      const filterType = activeTab == 1 ? 'allDevices' : 'myDevices'
      const resp = await fetchDeviceGlobalSearchData(query, filterType);
      setDeviceData(resp?.data?.globalSearchData)
      setIsLoading(false);
      setDataFlag(true)
      handle_toast({message: `Filtered devices data fetched`, status: 'success'})
    }
    catch (error) {
      setIsLoading(false)
      setDataFlag(true);
      handle_toast(error)
    }
  }

  const handleSave = async (formData) => {
    if(formData.productId){
      setIsLoading(true)
      try {
        const resp = await createDeviceData(formData)
        if (resp) {
          handle_toast({message: `New Device Added`, status: 'success'})
          fetchDevices();
        }
      }
      catch (error) {
        setIsLoading(false)
        handle_toast(error)
      }

    }
  }

  const handleEdit = async (formData) => {
    setIsLoading(true)
    try {
      const resp = await updateDeviceData(formData)
      if (resp?.status == 200) {
        handle_toast({message: `Device Updated`, status: 'success'})
        fetchDevices();
      }
    }
    catch (error) {
      setIsLoading(false)
      handle_toast(error)
    }
  }

  const handleDelete = async (formData) => {
    setIsLoading(true)
    try {
      const resp = await deleteDeviceData(formData)
      if (resp?.status == 200) {
        handle_toast({message: `Device Deleted`, status: 'success'})
        fetchDevices();
      }
    }
    catch (error) {
      setIsLoading(false)
      handle_toast(error)
    }
  }

  const excelExport = () => {
    exportToExcel(deviceData, devicesData.deviceHeaders, 'Device')
  }

  const toggleModal = () => {
    setModalVisible(!modalVisible);
  }

  const validateJsonData = (jsonData) => {
    let errorMessages = [];
    let macAddressErrors = [];
    for (let i = 0; i < jsonData.devices.length; i++) {
      const device = jsonData.devices[i];
      if (!isValidMacAddress(device.macAddress)) {
        macAddressErrors.push(i);
      }
      for (const key in device) {
        if (device[key].trim() === '') {
          errorMessages.push(`Empty value for key "${key}" at index ${i}`);
        }
      }
    }
    if (errorMessages.length > 0) {
      const errorMessage = errorMessages.join('\n');
      return { valid: false, message: errorMessage, macAddressErrors };
    }
    return { valid: true };
  };
  
  const isValidMacAddress = (macAddress) => {
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    return macRegex.test(macAddress);
  };
  
  const handleUpload = async (file) => {
    setIsLoading(true);
  
    const reader = new FileReader();
    reader.onload = async (event) => {
      const fileContent = event.target.result;
      try {
        const jsonData = JSON.parse(fileContent);
        const validationResult = validateJsonData(jsonData);
        if (validationResult.valid) {
          const resp = await uploadBulkDevice(jsonData);
          if (resp?.status == 200) {
            handle_toast({message: resp?.data?.message, status: 'success'})
            fetchDevices();
          }
        } else {
          setIsLoading(false);
          let errorMessage = validationResult.message;
  
          if (validationResult.macAddressErrors.length > 0) {
            const macErrorIndices = validationResult.macAddressErrors.join(', ');
            const macErrorMessage = `Invalid macAddress at index ${macErrorIndices}. Correct format is 00-00-00-00-00-00\n\n`;
            errorMessage = macErrorMessage + errorMessage;
          }
          handle_toast({message: errorMessage, status: 'error'})
        }
      } catch (error) {
        setIsLoading(false);
        handle_toast(error)
      }
    };
    reader.readAsText(file.file);
  };

  const bulkDeactivateAPI = async () => {
    setIsLoading(true)
    try {
      const resp = await deactivateDevice(deactivateData)
      if (resp?.status == 200) {
        handle_toast({message: `Device status updated`, status: 'success'})
        setDeactivateDeviceData([]);
        setDeactivateButtonToActive(false)
        fetchDevices();
      }
    }
    catch (error) {
      setIsLoading(false)
      handle_toast(error)
    }
  }

  useEffect(() => {
    if (deactivateData.length > 0 && !deactivateFlag) updateDeactivateFlag();
    if (deactivateData.length == 0) {
      setsubNavData([])
      setIsLoading(true);
      setDeactivateButtonToActive(false)
      setsubNavData(devicesData.deviceSubNav)
    }
  }, [deactivateData])


  const updateDeactivateFlag = () => {
    setsubNavData([])
    setIsLoading(true);
    const updatedData = JSON.parse(JSON.stringify(devicesData.deviceSubNav))
    updatedData.map((obj) => {
      if (obj.name == 'Deactivate') obj.disabled = false;
    })
    setsubNavData(updatedData)
    setDeactivateButtonToActive(true)
  }

  useEffect(() => {
    if (subNavData.length > 0) setIsLoading(false)
  }, [subNavData])

  return (
    <>
      <div className='containerSubNavCss'>
        {devicesData.deviceNavItem.map(({ name, id }) => {
          return (
            <a key={id} data-testid={`nav-${name}`} onClick={() => handleClickSelect(id)} className={activeTab === id ? 'active' : ''} >{`${name}`}</a>
          );
        })}
      </div>
      <div className='componentContainer'>
        {isLoading && <Loader />}
        {!isLoading && <SubNav data={subNavData} access={accessData} handleModalClick={toggleModal} sendReset={fetchDevices} excelExport={excelExport} handleDDSearch={fetchGlobalSearch} deactivate={bulkDeactivateAPI} ></SubNav>}
        <se-container>
          <se-divider option='vertical'></se-divider>
          <div style={{ display: 'block' }} className='tableContainer'>
            {
              <Table columns={devicesData.deviceHeaders} access={accessData} body={deviceData} modalData={devicesData.deviceModalData} onSave={handleEdit} onDelete={handleDelete} page='device' selection={true} selectedData={setDeactivateDeviceData} />
            }
          </div>
        </se-container>
        {modalVisible && <DisplayModal toggleModal={toggleModal} data={devicesData.deviceModalData} bulkData={devicesData.deviceModalBulkData} source="create" onSave={handleSave} page="device" onBulkDeviceUpload={handleUpload} />}
      </div>
    </>
  );
};
export default Device;

